import { useState } from 'react';
import { CheckoutService, CheckoutDiagnosticData } from '../../services/checkoutService';
import { ShippingRatesDiagnosticData } from '../../types';
import { JsonTree } from '../ui';

function DiagSection({ title, data }: { title: string; data: unknown }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="border border-gray-600 rounded overflow-hidden">
      <button
        type="button"
        className="w-full text-left px-4 py-2 bg-gray-800 text-gray-200 text-xs font-mono font-semibold hover:bg-gray-700 flex items-center gap-2"
        onClick={() => setOpen(!open)}
      >
        <span>{open ? '▼' : '▶'}</span>
        {title}
      </button>
      {open && <JsonTree data={data} />}
    </div>
  );
}

interface DiagnosticPanelProps {
  ratesDiagnostic?: ShippingRatesDiagnosticData | null;
}

export function DiagnosticPanel({ ratesDiagnostic }: DiagnosticPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<CheckoutDiagnosticData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    setLoading(true);
    setError(null);

    try {
      setData(await CheckoutService.getDiagnosticData());
    } catch {
      setError('Failed to load diagnostic data.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 border-t border-gray-300 pt-4">
      <button
        type="button"
        onClick={handleToggle}
        className="px-4 py-2 text-xs font-mono font-semibold bg-gray-700 text-gray-100 rounded hover:bg-gray-600 transition-colors"
      >
        {isOpen ? '▲' : '▼'} DIAGNOSTIC INFO
      </button>

      {isOpen && (
        <div className="mt-4 space-y-2">
          {loading && (
            <p className="text-xs text-gray-500 font-mono">Loading…</p>
          )}
          {error && (
            <p className="text-xs text-red-600 font-mono">{error}</p>
          )}
          {data && (
            <>
              <DiagSection title="User" data={data.user} />
              <DiagSection title="Cart" data={data.cart} />
              <DiagSection title="Cart Items" data={data.cartItems} />
              <DiagSection title="Orders" data={data.orders} />
              <DiagSection title="Totals" data={{
                totals: data.totals,
                discounts: data.discounts,
                netTotals: data.netTotals,
                quantityDiscountSavings: data.quantityDiscountSavings,
              }} />
              <DiagSection title="Flags & Payment Types" data={{
                flags: data.flags,
                paymentTypes: data.paymentTypes,
              }} />
              <DiagSection title="Shipping Rates" data={ratesDiagnostic} />
              {((data.promoErrors as unknown[])?.length > 0 || (data.configurationWarnings as unknown[])?.length > 0) && (
                <DiagSection title="Warnings & Errors" data={{
                  promoErrors: data.promoErrors,
                  configurationWarnings: data.configurationWarnings,
                }} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
